import {environment} from "../../../environments/environment";
import * as lpn from 'google-libphonenumber';
import {Product} from "../../core/models/product";
import {CdsLocalizedInputValue} from "@carool1/ngx-carool-ds";
import {MaybeAsync, Router, UrlSegment, UrlTree} from "@angular/router";
import {UiUtils} from "./ui.utils";
import moment from "moment";
import {isEmpty, isNaN, isNumber} from "lodash";
import * as Sentry from "@sentry/angular";
import {Picture} from "../../core/models/picture";
import {from, isObservable, Observable, of} from "rxjs";
import {CaptureContext} from "@sentry/angular";
import {HttpErrorResponse} from "@angular/common/http";
import {AppOrigin} from "../../core/dto/app-origin";

export class Utils {

  private static readonly phoneUtil = lpn.PhoneNumberUtil.getInstance();
  private static readonly EMAIL_REGEX = /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/;
  private static readonly COMPANY_KEY = 'company';

  static getClosestTimeInterval(intervalMinutes: number) {
    const duration = moment.duration(intervalMinutes, 'minutes');
    return moment(Math.ceil((+moment()) / (+duration)) * (+duration));
  }

  private static isGBLocale() {
    return UiUtils.getBrowserLanguage() === 'en-GB';
  }

  static getDefaultCountry(countries?: CdsLocalizedInputValue[]) {
    if(!countries) return;
    const locale = this.isGBLocale() ? 'gb' : UiUtils.getBrowserLanguage();
    const country = countries.find((country) => country.countryISO === locale);
    return country || countries[0];
  }

  static isOS(): boolean {
    return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
        'MacIntel'
      ].includes(navigator.platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
      || (navigator.userAgent.includes("MacIntel") && "ontouchend" in document)
  }

  private static handleLocalDomainEnvironment(domain: string){
    const subCompany = this.extractSubcompany(domain);
    if (!environment.production && (domain.includes('localhost') || domain.includes('192.168') || domain.includes('127.0.0.1')) && environment.domain) {
      return subCompany ? environment.domain + '/' + this.COMPANY_KEY +'/' + subCompany : environment.domain;
    }
    return domain;
  }

  public static extractSubcompany(url: string = window.location.href) {
    const decodedUrl = decodeURIComponent(url);
    const companyIndex = decodedUrl.indexOf(`${this.COMPANY_KEY}/`);

    if (companyIndex === -1) {
      return undefined;
    }

    const subPath = decodedUrl.substring(companyIndex + `${this.COMPANY_KEY}/`.length);
    return subPath.split(/[/?#]/)[0];
  }

  public static getSubCompanyPath(url: string = window.location.href){
    const companySlug = this.extractSubcompany(url);
    return companySlug ? `/${this.COMPANY_KEY}/${companySlug}` : '';
  }

  static getCompanyFromDomain(domain: string = window.location.href) {
    domain = this.handleLocalDomainEnvironment(domain);
    const matchProd = domain.match(/https:\/\/([a-z0-9|-]*).ca-rool.com/);
    const matchOtherPlatform = domain.match(/https:\/\/([a-z0-9|-]*).(dev|val).ca-rool.com/);

    let subdomain = '';
    if (matchProd) {
      subdomain = matchProd[1];
    } else if (matchOtherPlatform) {
      subdomain = matchOtherPlatform[1];
    }
    subdomain = subdomain === 'diag' ? 'carool' : subdomain;

    const paramsCompany = this.extractSubcompany(domain);
    if (paramsCompany) {
      subdomain = subdomain + '-' + paramsCompany;
    }
    return subdomain.toLowerCase();
  }

  static getDomain(domain: string = window.location.href){
    domain = domain.split('.com')[0];
    domain += '.com';
    domain = this.handleLocalDomainEnvironment(domain);
    return domain.toLowerCase();
  }

  static capitalizeFirstLetter(text: string) {
    return text[0].toUpperCase() + text.slice(1);
  }

  static isValidPhoneNumber(phone: string) {
    // Parse phone number as input - accept only phone number starting per +Country_Code
    if (!phone.startsWith('+')) {
      return false;
    }

    const number = this.parsePhoneNumber(phone);
    return number && this.phoneUtil.isPossibleNumber(number) && this.phoneUtil.isValidNumber(number);
  }

  static convertToE164(phone: string) {
    const number = this.parsePhoneNumber(phone, phone.startsWith('+') ? undefined : 'FR');
    return number ? this.phoneUtil.format(number, lpn.PhoneNumberFormat.E164) : '';
  }

  static parsePhoneNumber(phone?: string, region?: string) {
    try {
      return this.phoneUtil.parse(phone, region);
    } catch (e) {
      console.error(e);
      Sentry.captureException(e, {extra: {phone, region}});
      return undefined;
    }
  }

  static isValidEmail(email: string) {
    return this.EMAIL_REGEX.test(email);
  }

  static getAppOrigin(product: Product) {
    let appOrigin: AppOrigin | undefined;
    switch (product) {
      case Product.SCANNER:
        appOrigin = 'TyreScanner';
        break;
      case Product.DIAGNOSIS:
        appOrigin = 'Diagnosis';
        break;
      case Product.COLLECT:
        appOrigin = 'WebappCollect';
        break;
    }
    return appOrigin;
  }

  static replaceLastUrlSegment(url: string, newSegment: string, router: Router){
    const currentUrlTree = router.parseUrl(url);
    const segments = currentUrlTree.root.children['primary'].segments;
    if(segments.length > 0){
      segments[segments.length - 1] =  new UrlSegment(newSegment, {});
      const segmentPaths = segments.map((segment) => segment.path)
      return router.createUrlTree([...segmentPaths]);
    }
    return new UrlTree();
  }

  static isBlank(value: any) {
    return isEmpty(value) && !isNumber(value) || isNaN(value)
  }

  static isDevelopment = () => {
    return ['localhost', '.dev.'].some(t => window.location.host.includes(t));
  }

  static sanitizePictures(pictures: Picture[] | Picture): any[] | any{
    const cloned = structuredClone(pictures);
    if (Array.isArray(cloned))
      cloned.forEach((picture) => {
        // @ts-ignore
        picture['byteLength'] = picture.data?.byteLength;
        picture.data = undefined;
      });
    else {
      // @ts-ignore
      cloned['byteLength'] = cloned.data?.byteLength;
      cloned.data = undefined;
    }
    return cloned;
  }
}

export const hasElements = (array?: any[]): boolean => {
  return !!array && array.length > 0;
}

export const getRedirection = (product: Product, fleet?: string, company?: string) => {
  let redirection = `/${product}`;
  if (fleet && fleet !== 'public') {
    redirection += `/${fleet}`;
  }
  if (company) {
    redirection += `/company/${company}`;
  }
  return redirection;
}

export const ensureObservable = <T>(value: MaybeAsync<T>): Observable<T> => {
  if (isObservable(value)) {
    return value;
  } else if (value instanceof Promise) {
    return from(value);
  } else {
    return of(value);
  }
}

export const logSentryError = (error: HttpErrorResponse, message: string, context?: CaptureContext): void => {
  if (error.status === 0) {
    Sentry.captureMessage(message, context);
  } else {
    Sentry.captureException(new Error(message), context);
  }
}
